import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';

import { TableParamsProps } from 'business/providers/table-params';
import { DEFAULT_PAGE_SIZE } from 'business/quality-control/constants';
import type { RingComplianceReportFilters } from 'business/quality-control/pages/ring-compliance-report/providers/filters';

import { RingComplianceStatus } from './data/types';
import { ORDER_FIELD, ORDER_TYPE } from '..';

const SEARCH_PARAM_KEY_MAP = {
  ringNumberMin: 'ringNumberMin',
  ringNumberMax: 'ringNumberMax',
  status: 'status',
  complianceDateStart: 'complianceDateStart',
  complianceDateEnd: 'complianceDateEnd',
  approvalDateStart: 'approvalDateStart',
  approvalDateEnd: 'approvalDateEnd',
  page: 'page',
  pageSize: 'pageSize',
  sortField: 'sortField',
  sortOrder: 'sortOrder',
} as const;

const DATE_FORMAT = 'YYYY-MM-DD';

const parseNumberParam = (raw: string | null) => {
  if (raw === null) {
    return null;
  }
  const value = Number(raw);
  return Number.isNaN(value) ? null : value;
};

const parseDateParam = (raw: string | null) => {
  if (raw === null) {
    return null;
  }
  const value = dayjs(raw, DATE_FORMAT, true);
  return value.isValid() ? value : null;
};

export const useRingComplianceReportFilterParams = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const initialFilters: RingComplianceReportFilters = {
    ringNumberRange: [
      parseNumberParam(searchParams.get(SEARCH_PARAM_KEY_MAP.ringNumberMin)),
      parseNumberParam(searchParams.get(SEARCH_PARAM_KEY_MAP.ringNumberMax)),
    ],
    status: searchParams.getAll(
      SEARCH_PARAM_KEY_MAP.status,
    ) as RingComplianceStatus[],
    complianceDateRange: [
      parseDateParam(
        searchParams.get(SEARCH_PARAM_KEY_MAP.complianceDateStart),
      ),
      parseDateParam(searchParams.get(SEARCH_PARAM_KEY_MAP.complianceDateEnd)),
    ],
    approvalDateRange: [
      parseDateParam(searchParams.get(SEARCH_PARAM_KEY_MAP.approvalDateStart)),
      parseDateParam(searchParams.get(SEARCH_PARAM_KEY_MAP.approvalDateEnd)),
    ],
  };

  const pageParam = parseNumberParam(
    searchParams.get(SEARCH_PARAM_KEY_MAP.page),
  );
  const pageSizeParam = parseNumberParam(
    searchParams.get(SEARCH_PARAM_KEY_MAP.pageSize),
  );
  const sortOrderParam = searchParams.get(SEARCH_PARAM_KEY_MAP.sortOrder);

  const initialCurrentPage = pageParam ?? undefined;
  const initialPageSize = pageSizeParam ?? DEFAULT_PAGE_SIZE;
  const initialSortField =
    searchParams.get(SEARCH_PARAM_KEY_MAP.sortField) ?? ORDER_FIELD;
  const initialSortOrder: 'ascend' | 'descend' =
    sortOrderParam === 'ascend' || sortOrderParam === 'descend'
      ? sortOrderParam
      : ORDER_TYPE;

  const updateSearchParams = (
    filters: RingComplianceReportFilters,
    tableParams: TableParamsProps,
  ) => {
    const params = new URLSearchParams();

    const [ringNumberMin, ringNumberMax] = filters.ringNumberRange ?? [];
    if (ringNumberMin != null) {
      params.set(SEARCH_PARAM_KEY_MAP.ringNumberMin, String(ringNumberMin));
    }
    if (ringNumberMax != null) {
      params.set(SEARCH_PARAM_KEY_MAP.ringNumberMax, String(ringNumberMax));
    }

    filters.status.forEach((status: RingComplianceStatus) => {
      params.append(SEARCH_PARAM_KEY_MAP.status, status);
    });

    const [complianceStart, complianceEnd] = filters.complianceDateRange ?? [];
    if (complianceStart) {
      params.set(
        SEARCH_PARAM_KEY_MAP.complianceDateStart,
        complianceStart.format(DATE_FORMAT),
      );
    }
    if (complianceEnd) {
      params.set(
        SEARCH_PARAM_KEY_MAP.complianceDateEnd,
        complianceEnd.format(DATE_FORMAT),
      );
    }

    const [approvalStart, approvalEnd] = filters.approvalDateRange ?? [];
    if (approvalStart) {
      params.set(
        SEARCH_PARAM_KEY_MAP.approvalDateStart,
        approvalStart.format(DATE_FORMAT),
      );
    }
    if (approvalEnd) {
      params.set(
        SEARCH_PARAM_KEY_MAP.approvalDateEnd,
        approvalEnd.format(DATE_FORMAT),
      );
    }

    params.set(SEARCH_PARAM_KEY_MAP.page, String(tableParams.current));
    params.set(SEARCH_PARAM_KEY_MAP.pageSize, String(tableParams.pageSize));
    if (tableParams.sortField) {
      params.set(SEARCH_PARAM_KEY_MAP.sortField, String(tableParams.sortField));
    }
    if (tableParams.sortOrder) {
      params.set(SEARCH_PARAM_KEY_MAP.sortOrder, tableParams.sortOrder);
    }

    setSearchParams(params, { replace: true });
  };

  return {
    initialFilters,
    initialCurrentPage,
    initialPageSize,
    initialSortField,
    initialSortOrder,
    updateSearchParams,
  };
};
